import { useRef, useEffect, useState } from "react";

export default function TableTopControls({
  search,
  setSearch,
  rowsPerPage,
  setRowsPerPage
}) {
  const inputRef = useRef(null);
  const [localSearch, setLocalSearch] = useState(search ?? "");

  // Keep latest setSearch in a ref so the debounce effect below
  // does NOT retrigger when the parent passes a new function ref
  // on every render (which was causing page resets).
  const setSearchRef = useRef(setSearch);
  useEffect(() => {
    setSearchRef.current = setSearch;
  }, [setSearch]);

  // Sync when parent updates externally
  useEffect(() => {
    setLocalSearch(search ?? "");
  }, [search]);

  // Debounce logic (400ms) — depends ONLY on localSearch so it doesn't
  // fire on unrelated re-renders (e.g. page changes).
  useEffect(() => {
    if (typeof setSearchRef.current !== "function") return;

    const next = (localSearch ?? "").toUpperCase();

    // Skip firing when nothing actually changed compared to parent's search
    if (next === (search ?? "")) return;

    const handler = setTimeout(() => {
      if (typeof setSearchRef.current === "function") {
        setSearchRef.current(next);
      }
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  // Maintain focus
  useEffect(() => {
    const input = inputRef.current;
    if (input && document.activeElement !== input) return;
    input?.focus();
  }, [localSearch]);

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 p-4 border-b border-slate-200">

      {typeof setSearch === "function" && (
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4">

          <input
            ref={inputRef}
            type="text"
            placeholder="Search..."
            value={localSearch}
            maxLength={30}
            onChange={(e) => {
              const value = e.target.value;
              const sanitized = value.replace(/[^a-zA-Z0-9.@#\-_ ]/g, '');

              setLocalSearch(sanitized);
            }}
            className="w-full sm:w-64 px-3 py-2 pr-8 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />

          {localSearch && (
            <button
              type="button"
              onClick={() => {
                setLocalSearch('');
                if (typeof setSearchRef.current === "function") {
                  setSearchRef.current('');
                }
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 w-full sm:w-auto">
        <label className="text-sm text-slate-600 whitespace-nowrap">
          Page Size:
        </label>
        <select
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(Number(e.target.value))}
          className="w-full sm:w-auto px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>

    </div>
  );
}
